import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { approvalStateOf, type ApprovalRide } from '../utils/rideActions';
import { resendGuestRideApproval, ridesApiError } from '../services/ridesApi';

interface GuestRideApprovalNoteProps {
  ride: ApprovalRide & { id: number };
  /** Show the Resend control. Off where the viewer can't manage rides. */
  canResend?: boolean;
  dense?: boolean;
}

const approverName = (ride: ApprovalRide) => ride?.approver?.display_name || 'the approver';

/**
 * One line on a ride held for manager approval: who it is waiting on, or why it was denied.
 * Renders nothing for rides that were never held, so rooftops without approval see no change.
 */
const GuestRideApprovalNote: React.FC<GuestRideApprovalNoteProps> = ({ ride, canResend = false, dense = false }) => {
  const [sending, setSending] = React.useState(false);
  const state = approvalStateOf(ride);
  if (state === 'none' || state === 'approved') return null;

  const resend = async () => {
    setSending(true);
    try {
      await resendGuestRideApproval(ride.id);
      toast.success(`Sent ${approverName(ride)} another reminder.`);
    } catch (err) {
      const { status, message, retryAfterSeconds } = ridesApiError(err);
      toast.error(
        status === 429 && retryAfterSeconds
          ? `${approverName(ride)} was just notified. Try again in ${retryAfterSeconds} seconds.`
          : message || 'Could not resend the approval request.'
      );
    } finally {
      setSending(false);
    }
  };

  if (state === 'denied') {
    return (
      <Typography variant='caption' sx={{ color: 'error.main', display: 'block', lineHeight: 1.3 }}>
        Denied by {approverName(ride)}
        {ride.approval_decision_reason ? `: ${ride.approval_decision_reason}` : ''}
      </Typography>
    );
  }

  // A waiting ride the requester cancelled is just cancelled; the status badge already says so.
  if (ride?.status === 'cancelled') return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: dense ? 'nowrap' : 'wrap' }}>
      <Typography variant='caption' sx={{ color: 'warning.dark', fontWeight: 600, lineHeight: 1.3 }}>
        Waiting for approval from {approverName(ride)}
      </Typography>
      {canResend && (
        <Button
          size='small'
          variant='text'
          disabled={sending}
          onClick={e => {
            e.stopPropagation();
            resend();
          }}
          sx={{ minWidth: 0, p: 0, fontSize: 12 }}
        >
          {sending ? 'Sending...' : 'Resend'}
        </Button>
      )}
    </Box>
  );
};

export default GuestRideApprovalNote;
